import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { Content } from 'app/constants';
import {
  deleteAuthor,
  loadAuthorProfile,
  resetOutletProfile,
  resetSearch,
  resetTopicProfile,
  setAuthorDialog,
  setCoverages,
  setFacetsSearch,
  setInputSearchPlaceholder,
  setSearch,
  showAuthorManagerDialog,
  startLoading,
  tabControl,
  headerMenuActive,
  loadAuthorsOutlets,
} from 'app/redux/actions';
import { AuthorService, SearchFilterDefaultsService } from 'app/services';
import { eq, orderBy, reverse } from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { SidebarList } from 'app/models';
import { TOPIC } from '../../constants';
import { ToastNotificationService } from 'app/components';

@Component({
  selector: 'app-sidebar-author-list',
  template: `
    <app-sidebar-entity-table
      kind="author"
      [items]="authors"
      [allItems]="allAuthors"
      (selected)="selectedItem($event)"
      (edit)="openEditAuthorManagerDialog($event)"
    ></app-sidebar-entity-table>
    <!-- <span *ngIf="loading">Loading..</span>
    <span *ngIf="loaded">No results</span> -->
  `,
})
export class SidebarAuthorListComponent implements OnInit {
  public authors: SidebarList[];
  public allAuthors: SidebarList[];
  public selectItem: any;
  public sort: any;
  public showScrollbar: boolean;
  public projectId: number;
  public loading: boolean;
  public loaded: boolean;
  public currentProject: any;
  public keywords: string;
  public isEdit: boolean;
  public isUnfollow: boolean;
  public parentName: string;
  public reordering: boolean;

  @ViewChildren('sortNameIcon, sortCategoryIcon', {
    read: ElementRef,
  })
  sortIcons: QueryList<ElementRef>;

  constructor(
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private authorService: AuthorService,
    private notificationService: ToastNotificationService,
    private searchFilterDefaults: SearchFilterDefaultsService
  ) {
    this.isEdit = false;
    this.isUnfollow = false;
    this.keywords = null;
    this.showScrollbar = false;
    this.loading = false;
    this.loaded = false;
    this.authors = [];
    this.allAuthors = [];
    this.parentName = Content.Author;
    this.reordering = false;
  }

  ngOnInit() {
    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        const { keywords } = searchBody;
        this.keywords = keywords;
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });

    this.store
      .select('authorsOutletsList')
      .subscribe(({ authors, loading, loaded, errors }) => {
        this.loading = loading;
        this.loaded = loaded;
        if (authors && authors.length > 0 && loaded) {
          this.allAuthors = authors.map(
            (item) =>
              new SidebarList({
                title: item.name,
                subTitle: item.categoryName || item.label?.name,
                picture: this.loadPicture(item.partyId),
                loadingIcon: false,
                hasPicture: true,
                parentObject: {
                  ...item,
                  categoryName: item.categoryName || item.label?.name,
                  categoryId: item.label?.id,
                  name: item.name,
                  id: item.id,
                  partyId: item.partyId,
                },
                parentName: this.parentName,
              })
          );
          const visibleAuthors = this.allAuthors.filter(
            (item) => item.subTitle !== 'Ignore'
          );
          this.authors = [
            ...visibleAuthors.filter((item) => !!item.subTitle),
            ...visibleAuthors.filter((item) => !item.subTitle),
          ];
        } else {
          this.allAuthors = [];
          this.authors = [];
        }
      });

    this.store
      .select('authorDialog')
      .subscribe(({ authorId, loading, loaded }) => {
        if (authorId) {
          this.loadingAuthorDialog(authorId, loading, loaded);
        }
      });
  }

  public selectedItem(item) {
    // Wait for the sidebar's close transition before starting the search UI.
    window.setTimeout(() => this.searchForAuthor(item), 250);
  }

  private searchForAuthor(item) {
    this.store.dispatch(resetSearch({ onReset: true }));

    // TODO: Open coverage section
    this.store.dispatch(tabControl({ index: 1 }));
    this.store.dispatch(headerMenuActive({ menuActive: 'Home' }));

    // TODO: Hide outlet/topic profile
    this.store.dispatch(resetOutletProfile());
    this.store.dispatch(resetTopicProfile());

    // TODO: Show outlet profile
    this.store.dispatch(
      loadAuthorProfile({
        authorId: item.partyId,
      })
    );

    // TODO: Run search to content items on converage section
    this.store.dispatch(
      setCoverages({
        searchBody: this.searchFilterDefaults.createSearch({
          keywords: this.keywords,
          authors: [item.partyId],
          outlets: [],
        }),
        topicId: null,
        sort: 'recent',
        page: 0,
        size: 50,
        isSearch: true,
      })
    );

    const search = this.searchFilterDefaults.createSearch({
      keywords: null,
      facets: { OUTLETS: null, COMPANIES: null, TOPICS: null },
      authors: [item.partyId],
      outlets: [],
    });

    // TODO: Show facets table
    this.store.dispatch(
      setFacetsSearch({
        searchBody: search,
        page: 0,
        isNewSearch: true,
      })
    );

    this.store.dispatch(
      setSearch({
        search: search,
      })
    );

    this.store.dispatch(
      setInputSearchPlaceholder({ placeholder: `${item.name} (Author)` })
    );

    // Clear the stale reset flag set at the top of this method so a freshly
    // mounted <app-search> (e.g. first click from the Report screen) does not
    // read onReset:true and undo the tabControl above.
    this.store.dispatch(resetSearch({ onReset: false }));

    // this.store.dispatch(
    //   setAuthorDialog({
    //     authorId: item.partyId,
    //     projectSelected: this.currentProject,
    //     keywords: this.keywords,
    //     sort: 'recent'
    //   })
    // );
  }

  public reorderAuthors(reordered: SidebarList[]) {
    const previous = this.authors;
    this.authors = reordered;
    this.reordering = true;

    const reorderedItems = [...reordered];
    const completeOrder = this.allAuthors.map((item) =>
      item.subTitle !== 'Ignore' ? reorderedItems.shift() : item
    );
    const orderedIds = completeOrder.map((item) => item.parentObject.id);
    this.authorService
      .updateAuthorOrder(this.currentProject.value, orderedIds)
      .subscribe(
        () => {
          this.reordering = false;
          this.store.dispatch(
            loadAuthorsOutlets({ projectId: this.currentProject.value })
          );
        },
        () => {
          this.authors = previous;
          this.reordering = false;
          this.notificationService.showMessage(
            'Unable to save author order.',
            'Error'
          );
        }
      );
  }

  loadingAuthorDialog(authorId, loading, loaded) {
    const index = this.authors.findIndex(({ parentObject }) =>
      eq(parentObject.partyId, authorId)
    );
    if (index < 0) {
      return;
    }
    if (loading) {
      this.authors[index].loadingIcon = true;
    }
    if (loaded) {
      this.authors[index].loadingIcon = false;
    }
  }

  openCreateAuthorManagerDialog() {
    this.store.dispatch(showAuthorManagerDialog({ author: null }));
  }

  openEditAuthorManagerDialog(author) {
    this.store.dispatch(showAuthorManagerDialog({ author }));
  }

  handleUnfollow(value) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to unfollow this author?',
      header: 'Unfollow Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'unfollowConfirmation',
      accept: () => {
        this.unfollow(value);
      },
      reject: (type) => {
        // handle rejected option ConfirmEventType.CANCEL
      },
    });
  }

  public unfollow(value) {
    const payload = {
      id: value.partyId,
      name: value.name,
      userProject: this.currentProject,
    };
    this.store.dispatch(
      deleteAuthor({
        payload,
      })
    );
  }

  public loadPicture(id) {
    return this.authorService.getAuthorPicture(id);
  }
}
